// R216 — WHICH ANSWER MAY PAINT.
//
// **THIS HOUSE HAD FOUR SPELLINGS OF ONE LAW AND THE FOURTH WAS NOTHING AT ALL.** A surface that
// re-reads a route when a member changes a filter has one rule to obey: *only the newest read may
// paint*. Measured across `src/App.tsx` and `src/components/`, it was written four different ways:
//
//   1. `readSeq` — a `useRef` counter in `OwnerFinancials.tsx`, ITEM 10's repair, with its whole
//      reasoning written out. **That one is right, and this module is it, lifted.**
//   2. `let cancelled = false` + a cleanup — the Angels drawer (R99).
//   3. `let active = true` + a cleanup — `useBookSearch` (R93).
//   4. **NOTHING** — `SpellbookExperience`, whose browse is keyed on thirteen tappable categories.
//
// **THE CONJURE SPELLBOOK IS THE ONE THAT PAID FOR IT, AND IT UNDOES R70's OWN LAW BY ORDERING.**
// That round exists because a failed read must never paint an empty book: *"a refusal NEVER paints
// — an unread book is not an empty one — and it always SAYS SO."* With no sequencing, ordering is
// enough to break both halves of it. She taps Money, the read refuses; she taps Love, which loads
// perfectly; Money's refusal lands last and the book prints **"we could not open it" over a
// category that is sitting right there**. The other way round, a stale category's spells render
// under a different active tab, silently, with nothing anywhere red.
//
// **AND THE CLEANUP IDIOM COULD NOT HAVE FIXED IT, WHICH IS WHY THE COUNTER IS THE CARRIER AND NOT
// A PREFERENCE.** A `cancelled` flag lives in an effect's closure and is flipped by React's own
// cleanup, so it can only ever guard reads THAT EFFECT STARTED. The spellbook's `loadSpells` has
// **two callers** — the category effect, and the refresh fired after a generation is delivered —
// and no cleanup relationship exists between them. A counter on the surface guards both. That
// second caller is also the expensive one: it runs right after `turnTo(sp)` opens the spell she has
// just paid a generation for, and a stale list landing behind it leaves that spell out of the index
// and **both page arrows dead on the working she just bought**.
//
// **IT IS A COUNTER ON A REF AND NEVER ON STATE, WHICH IS D-5's RULING ONE ROOM OVER:** a state
// guard is read from the render that BOUND the handler, so two filter taps inside one frame both
// believe they are newest, and the guard passes every presence check there is while changing
// nothing. `ReadSeq` is `{ current: number }` **because that is a `useRef`'s own shape** — the
// component hands its ref straight over and a gate hands a plain object literal, so every ordering
// this room can produce is reachable with no browser and no React (R46's `doFetch`-is-a-parameter
// pattern, applied to a ref).
//
// **IT FAILS CLOSED, AND THE DIRECTION IS THE RULING (R19/R8-4).** A law that wrongly REFUSES a
// paint leaves the surface visibly empty and somebody reports it within the hour; one that wrongly
// ALLOWS a paint is this round's own defect, and it renders perfectly. So a counter that has issued
// nothing may paint nothing, and a ticket that is not a real issued number is refused rather than
// coerced — `Number(true)` is 1 and `Number("")` is 0 (R136/R137), and either would hand a
// hand-typed ticket the newest read's answer.
export type ReadSeq = { current: number };

// Take a ticket. The counter moves FIRST, so the read that is starting is the newest one from this
// instant — never after the request, which would leave a window where an older answer is still the
// newest thing the surface knows about.
export function beginRead(seq: ReadSeq): number {
  const next = issued(seq) + 1;
  seq.current = next;
  return next;
}

// R221 — **A CLOSE THAT A PENDING READ CAN UNDO IS NOT A CLOSE.**
//
// R216 answered *which answer may paint*. It did not answer the other half, because the four
// surfaces it repaired all paint INTO the page: a stale list is replaced by the next one and the
// worst it can do is be wrong for a moment. **The owner dashboard's member detail is a MODAL**, and
// a modal has a state the others do not have — SHUT — reached by two doors that both write
// `setDetail(null)`: her Close, and a navigation (`goToView` closes it on purpose, so a tap on
// another tab does not leave somebody's payments hanging over it).
//
// **`setDetail(null)` IS NOT ENOUGH ON ITS OWN, AND THAT IS THE ROUND.** An in-flight read is a
// PENDING `setDetail(...)`. She taps a member, the read is six sequential round trips, she gives up
// and navigates — and the answer lands afterwards and **RE-OPENS the modal over the surface she
// moved to**, carrying a member who has nothing to do with it. Closing it retires nothing, so she
// can close it as many times as she likes and the read still wins.
//
// **IT IS THE SAME ACT ON THE COUNTER AND A DIFFERENT ACT ON THE SCREEN, WHICH IS WHY IT IS NAMED**
// rather than left as a bare `beginRead` whose ticket nobody keeps. A ticket taken and dropped on
// the floor reads as a mistake — it is exactly what a careful reader deletes — and deleting it puts
// this defect straight back with a diff that reads as a tidy-up.
export function closeRead(seq: ReadSeq): void {
  beginRead(seq);
}

// **A SUPERSEDED READ IS DROPPED WHOLE — not merged, not used for the note, not used for the
// spinner** (ITEM 10's own words: *a stale note under fresh numbers is the same lie one line
// down*). That is a property of the CALL SITE and is held there; this answers the one question.
export function readMayPaint(seq: ReadSeq, ticket: unknown): boolean {
  const newest = issued(seq);
  // R53: PRESENCE, never truthiness. A counter that has issued nothing (0) has no newest read, so
  // a ticket of 0 — which is what a caller that never called `beginRead` would be holding — is
  // refused rather than matched against it.
  //
  // **AND THERE IS EXACTLY ONE ZERO TEST HERE, WHICH THE MUTATION RUN IS WHAT SETTLED (R19/R63).**
  // The first draft also refused `ticket < 1`, and with both present EITHER deletion is a
  // behavioural no-op — each covers the other, so the battery reported the clause about them
  // VACUOUS and was right. A line that cannot change an answer is a line no mutation can reach and
  // no reader can trust, so the redundant half is gone rather than kept for comfort.
  if (newest < 1) return false;
  if (typeof ticket !== "number" || !Number.isInteger(ticket)) return false;
  return ticket === newest;
}

// An unreadable counter has issued nothing, which is the closed direction above.
function issued(seq: ReadSeq): number {
  const n = seq && typeof seq.current === "number" ? seq.current : 0;
  return Number.isInteger(n) && n >= 0 ? n : 0;
}
